// app/(superadmin)/overview/page.tsx


import React, { useState, useEffect, useCallback } from "react";
import PageTitle from "../../../components/ui/PageTitle";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import Button from "../../../components/ui/Button";
import { getDashboardSummary } from "../../../lib/apiClient";
import { DashboardSummaryResponse } from "../../../types";
import { getErrorMessage } from "../../../lib/utils";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faArrowDown,
  faSync,
  faUserShield
} from "@fortawesome/free-solid-svg-icons";
import {
  faUser,
  faListAlt,
  faClipboard,
  faMoneyBill1
} from "@fortawesome/free-regular-svg-icons";

const StatCard: React.FC<{
  title: string;
  value: string | number;
  description?: string;
  icon?: React.ReactNode;
  trend?: { value: number; isPositive: boolean };
}> = ({ title, value, description, icon, trend }) => (
  <div className="group bg-gradient-to-br from-dark-secondary to-dark-tertiary p-5 rounded-xl shadow-lg border border-dark-border hover:border-golden-accent/30 transition-all duration-300 hover:shadow-xl hover:shadow-golden-accent/5 min-h-[100px]">
    <div className="flex items-center space-x-4 h-full">
      {icon && (
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-golden-accent rounded-lg flex items-center justify-center text-text-on-accent shadow-lg">
            {icon}
          </div>
        </div>
      )}
      
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <h3 
          className="text-sm font-medium text-text-secondary mb-2 truncate font-montserrat cursor-help" 
          title={title}
        >
          {title}
        </h3>
        
        <p className="text-2xl font-bold text-text-primary group-hover:text-golden-accent transition-colors duration-300 leading-tight font-montserrat mb-1">
          {value}
        </p>
        
        {trend && (
          <div className={`inline-flex items-center text-xs font-medium mt-1 ${
            trend.isPositive 
              ? 'text-functional-success' 
              : 'text-functional-danger'
          }`}>
            <FontAwesomeIcon 
              icon={trend.isPositive ? faArrowUp : faArrowDown} 
              className="mr-1 text-xs" 
            />
            {Math.abs(trend.value)}%
          </div>
        )}
        
        {description && (
          <p className="text-xs text-text-secondary mt-0.5 truncate">{description}</p>
        )}
      </div>
    </div>
  </div>
);

export default function SuperAdminOverviewPage() {
  const [summary, setSummary] = useState<DashboardSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    console.log("Fetching dashboard summary...");
    setIsLoading(true);
    setError(null);
    try {
      const data = await getDashboardSummary();
      setSummary(data);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(`Failed to load dashboard summary: ${message}`);
      toast.error(`Error loading summary: ${message}`, {
        id: "fetch-summary-error",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const renderDashboardContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-10">
          <LoadingIndicator size="lg" text="Loading dashboard data..." />
        </div>
      );
    }
    if (error || !summary) {
      return (
        <div className="py-10">
          <ErrorMessage
            title="Error Loading Dashboard"
            message={error || "Could not fetch summary."}
          />
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Enhanced Stat Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
          <StatCard
            title="Total Users"
            value={summary.totalUsers ?? "N/A"}
            description="Verified users"
            icon={<FontAwesomeIcon icon={faUser} className="text-xl" />}
          />
          <StatCard 
            title="Total Admins" 
            value={summary.totalAdmins ?? "N/A"}
            icon={<FontAwesomeIcon icon={faUserShield} className="text-xl" />}
          />
          <StatCard
            title="Total Channels"
            value={summary.totalChannels ?? "N/A"}
            description={`${summary.totalActiveChannels} active`}
            icon={<FontAwesomeIcon icon={faListAlt} className="text-xl" />}
          />
          <StatCard
            title="Total Plans"
            value={summary.totalPlans ?? "N/A"}
            description={`${summary.totalActivePlans} active`}
            icon={<FontAwesomeIcon icon={faClipboard} className="text-xl" />}
          />
          <StatCard
            title="Active Subscriptions"
            value={summary.totalActiveSubscriptions ?? "N/A"}
            icon={<FontAwesomeIcon icon={faSync} className="text-xl" />}
          />
          <StatCard
            title="Recent Revenue (30d)"
            value={`₹${summary.recentRevenue?.toFixed(0) ?? "N/A"}`}
            icon={<FontAwesomeIcon icon={faMoneyBill1} className="text-xl" />}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Outer spacing */}
      <PageTitle
        title="System Overview"
        subtitle="Key metrics and performance across the platform."
      />
      {/* Refined subtitle */}
      {/* Main content container */}
      <div className="bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-primary p-8 md:p-10 rounded-2xl shadow-2xl border border-dark-border backdrop-blur-sm">
        {/* Enhanced container with gradient background */}
        
        {/* Render Dashboard Content (Stats, Charts, Errors, Loading) */}
        {renderDashboardContent()}
        
        {error && (
          <div className="mt-6 text-center">
            <Button onClick={fetchSummary} className="bg-golden-accent hover:bg-golden-accent/90">
              Retry
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
